import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import CampoTexto from '@/components/base/CampoTexto';
import CampoDinheiro from '@/components/base/CampoDinheiro';
import { formatarDinheiro } from '@/pages/gerenciar/utils';
import SeletorImagemProduto from './SeletorImagemProduto';
import { dataHoraParaISO, isoParaDataHoraLocal } from '../utils';
import type { PontoPainel } from '@/pages/fichas/types';
import type { ProdutoEditavel } from '../types';

interface Props {
  eventoId: number;
  pontos: PontoPainel[];
  produto: ProdutoEditavel | null;
  pontoId: number | null;
  aoFechar: () => void;
  aoSalvo: () => void;
}

const campoBase =
  'h-12 w-full rounded-2xl border border-secondary-300 bg-white px-4 text-[16px] text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500';

const campoDataHora =
  'mt-2 h-12 w-full rounded-2xl border border-secondary-300 bg-white px-4 text-[16px] text-foreground-950 outline-none transition-colors focus:border-primary-500';

// Formulário CURTO de produto: nome, preço, desenho/foto, barraca e estoque.
// "Mais opções" abre venda antecipada, só para inscritos, máximo e alerta.
export default function ModalProdutoCurto({
  eventoId,
  pontos,
  produto,
  pontoId,
  aoFechar,
  aoSalvo,
}: Props) {
  const [nome, setNome] = useState(produto?.nome ?? '');
  const [preco, setPreco] = useState(produto?.preco_centavos ?? 0);
  const [custo, setCusto] = useState(produto?.custo_centavos ?? 0);
  const tinhaCusto = (produto?.custo_centavos ?? 0) > 0;
  const [pontoSel, setPontoSel] = useState<number>(
    produto ? produto.ponto_id : pontoId ?? pontos[0]?.ponto_id ?? 0,
  );
  const [estoque, setEstoque] = useState(
    produto?.estoque_total != null ? String(produto.estoque_total) : '',
  );

  // "Mais opções"
  const [mostrarMais, setMostrarMais] = useState(false);
  const [vendaAntecipada, setVendaAntecipada] = useState(produto?.venda_antecipada ?? false);
  const [abreEm, setAbreEm] = useState(isoParaDataHoraLocal(produto?.abre_em ?? null));
  const [fechaEm, setFechaEm] = useState(isoParaDataHoraLocal(produto?.fecha_em ?? null));
  const [soInscritos, setSoInscritos] = useState(produto?.so_para_inscritos ?? false);
  const [maxPorPedido, setMaxPorPedido] = useState(
    produto?.max_por_pedido != null ? String(produto.max_por_pedido) : '20',
  );
  const [alertaRestam, setAlertaRestam] = useState(
    produto?.alerta_restam != null ? String(produto.alerta_restam) : '',
  );

  // Imagem (só em edição).
  const [imagemUrl, setImagemUrl] = useState(produto?.imagem_url ?? null);
  const [imagemSlug, setImagemSlug] = useState(produto?.imagem_slug ?? null);

  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function aoMudarImagem(url: string | null, slug: string | null) {
    setImagemUrl(url);
    setImagemSlug(slug);
  }

  async function salvar() {
    if (!nome.trim()) {
      setErro('Dê um nome ao produto.');
      return;
    }
    if (preco <= 0) {
      setErro('Coloque o preço do produto.');
      return;
    }
    if (vendaAntecipada && !abreEm) {
      setErro('Venda antecipada precisa da data e hora de abertura.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const estoqueNum = estoque.trim() === '' ? null : parseInt(estoque, 10);
      const alertaNum = alertaRestam.trim() === '' ? null : parseInt(alertaRestam, 10);
      const maxNum = maxPorPedido.trim() === '' ? 20 : parseInt(maxPorPedido, 10);
      const { error } = await supabase.rpc('loja_salvar_produto', {
        p_evento: eventoId,
        p_ponto: pontoSel,
        p_nome: nome.trim(),
        p_preco_centavos: preco,
        p_produto: produto ? produto.produto_id : null,
        p_categoria: produto?.categoria ?? 'outro',
        p_descricao: produto?.descricao ?? null,
        p_imagem_url: null,
        p_estoque_total: estoqueNum,
        p_so_para_inscritos: soInscritos,
        p_max_por_pedido: maxNum,
        p_vendas_abrem_em: vendaAntecipada ? dataHoraParaISO(abreEm) : null,
        p_vendas_fecham_em: vendaAntecipada ? dataHoraParaISO(fechaEm) : null,
        p_ativo: true,
        p_venda_antecipada: vendaAntecipada,
        p_alerta_restam: alertaNum,
        p_custo_centavos: custo > 0 ? custo : tinhaCusto ? -1 : null,
      });
      if (error) throw error;
      aoSalvo();
      aoFechar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={aoFechar} aria-hidden />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-[520px] flex-col overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl">
        <div className="flex flex-col items-center pt-2 sm:hidden">
          <span className="h-1.5 w-10 rounded-full bg-secondary-200" />
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <h3 className="text-[17px] font-bold text-foreground-950">
            {produto ? 'Editar produto' : 'Produto avulso'}
          </h3>
          <button
            type="button"
            onClick={aoFechar}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto px-5 py-4">
          {produto && (
            <SeletorImagemProduto
              eventoId={eventoId}
              produtoId={produto.produto_id}
              imagemUrl={imagemUrl}
              imagemSlug={imagemSlug}
              emoji={produto.emoji}
              nome={produto.nome}
              aoMudarImagem={aoMudarImagem}
            />
          )}

          <CampoTexto
            id="loja-nome-produto"
            name="nome"
            label="Nome"
            value={nome}
            onChange={setNome}
            required
            autoFocus
            placeholder="Ex.: Coca-Cola lata"
          />

          <CampoDinheiro
            id="loja-preco-produto"
            name="preco"
            label="Preço"
            centavos={preco}
            onChange={setPreco}
            required
          />

          <CampoDinheiro
            id="loja-custo-produto"
            name="custo"
            label="Custo"
            help="Quanto você paga por unidade. Só para calcular o lucro — ninguém vê."
            centavos={custo}
            onChange={setCusto}
            placeholder="não informado"
          />

          {custo > 0 && preco > 0 && custo > preco && (
            <div className="rounded-lg bg-warning-50 px-3 py-2 text-[13px] text-warning-700">
              Custo maior que o preço: você perde {formatarDinheiro(custo - preco)} por unidade.
            </div>
          )}

          <p className="text-[13px] text-foreground-500">
            {custo > 0
              ? `Lucro por unidade: ${formatarDinheiro(preco - custo)}`
              : 'Informe o custo para ver o lucro.'}
          </p>

          <div>
            <label
              htmlFor="loja-barraca-produto"
              className="block text-[14px] font-medium text-foreground-950"
            >
              Barraca
            </label>
            <select
              id="loja-barraca-produto"
              name="ponto"
              value={pontoSel}
              onChange={(e) => setPontoSel(Number(e.target.value))}
              className={`${campoBase} mt-2`}
            >
              {pontos.map((p) => (
                <option key={p.ponto_id} value={p.ponto_id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="loja-estoque-produto"
              className="block text-[14px] font-medium text-foreground-950"
            >
              Estoque total
            </label>
            <p className="mt-1 text-[13px] text-foreground-500">
              Deixe vazio se não quer contar (venda ilimitada).
            </p>
            <input
              id="loja-estoque-produto"
              name="estoque"
              type="text"
              inputMode="numeric"
              value={estoque}
              onChange={(e) => setEstoque(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="ilimitado"
              className={`${campoBase} mt-2`}
            />
          </div>

          {/* Mais opções */}
          <div>
            <button
              type="button"
              onClick={() => setMostrarMais((v) => !v)}
              className="flex cursor-pointer items-center gap-1.5 text-[14px] font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={mostrarMais ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
              </span>
              Mais opções
            </button>

            {mostrarMais && (
              <div className="mt-3 space-y-5 rounded-2xl bg-background-50 p-4">
                {/* Venda antecipada */}
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[15px] font-medium text-foreground-950">
                      Venda antecipada
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={vendaAntecipada}
                      onClick={() => setVendaAntecipada((v) => !v)}
                      className={`relative h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors ${
                        vendaAntecipada ? 'bg-primary-500' : 'bg-secondary-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${
                          vendaAntecipada ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="mt-1 text-[13px] text-foreground-500">
                    Vende antes de abrir as vendas do evento — ex.: almoço, camiseta.
                  </p>

                  {vendaAntecipada && (
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className="block text-[14px] font-medium text-foreground-950">
                          Abre em *
                        </label>
                        <input
                          type="datetime-local"
                          value={abreEm}
                          onChange={(e) => setAbreEm(e.target.value)}
                          className={campoDataHora}
                        />
                      </div>
                      <div>
                        <label className="block text-[14px] font-medium text-foreground-950">
                          Fecha em
                        </label>
                        <input
                          type="datetime-local"
                          value={fechaEm}
                          onChange={(e) => setFechaEm(e.target.value)}
                          className={campoDataHora}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Só para inscritos */}
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[15px] font-medium text-foreground-950">
                    Só para inscritos
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={soInscritos}
                    onClick={() => setSoInscritos((v) => !v)}
                    className={`relative h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors ${
                      soInscritos ? 'bg-primary-500' : 'bg-secondary-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform ${
                        soInscritos ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Máximo por pedido */}
                <div>
                  <label className="block text-[14px] font-medium text-foreground-950">
                    Máximo por pedido
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={maxPorPedido}
                    onChange={(e) => setMaxPorPedido(e.target.value.replace(/[^0-9]/g, ''))}
                    className={`${campoBase} mt-2`}
                  />
                </div>

                {/* Avisar quando restarem */}
                <div>
                  <label className="block text-[14px] font-medium text-foreground-950">
                    Avisar quando restarem
                  </label>
                  <p className="mt-1 text-[13px] text-foreground-500">
                    Em branco, avisamos quando restarem 20% (ou 5% de estoques pequenos).
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={alertaRestam}
                    onChange={(e) => setAlertaRestam(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="automático"
                    className={`${campoBase} mt-2`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {erro && (
          <div className="border-t border-background-100 bg-danger-50 px-5 py-3 text-[14px] text-danger-700">
            {erro}
          </div>
        )}

        <div className="border-t border-background-100 px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={salvar}
            disabled={salvando}
            className="flex min-h-[52px] w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 text-[15px] font-bold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          <button
            type="button"
            onClick={aoFechar}
            className="mx-auto mt-2 block cursor-pointer whitespace-nowrap text-[14px] font-medium text-foreground-500 transition-colors hover:text-foreground-800"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}